// Module-scoped store for the latest value of every MIDI CC the harness
// has heard. Read every audio scheduler tick by the Strudel `knob()`
// signal (see ../strudel/knobSignal.ts) and on every animation frame by
// any UI that paints "current knob position." Written by the MIDI access
// layer when an inbound `controlchange` message arrives.
//
// Values are normalised to the 0..1 range — the same convention as
// Strudel's other modulator signals (`rand`, `sine`). User code that
// wants a wider range chains `.range(lo, hi)`.
//
// Subscribers (the device picker, future MAP-gesture UI) are notified
// on every received CC so the "next CC heard" listening pattern is
// trivial — subscribe, capture the (channel, cc) tuple, unsubscribe.

export type CcKey = `${number}:${number}`; // `${channel}:${cc}`

export type CcEvent = {
  channel: number; // 0..15
  cc: number; // 0..127
  value: number; // 0..1, normalised
  rawValue: number; // 0..127, raw MIDI byte
  /** Performance.now() when the message was received. */
  timestamp: number;
};

type Listener = (event: CcEvent) => void;

const values = new Map<CcKey, number>();
const listeners = new Set<Listener>();

/** Build the storage key for a (channel, cc) pair. Exported so consumers
 *  that need to read a specific value with the same key the writer used
 *  can stay byte-equal — `${channel}:${cc}` is small enough that a typo
 *  would silently miss. */
export function ccKey(channel: number, cc: number): CcKey {
  return `${channel}:${cc}`;
}

/** Latest seen value for the given (channel, cc), 0..1, or null if no
 *  message has arrived yet. Cheap — one Map.get. */
export function getCcValue(channel: number, cc: number): number | null {
  const v = values.get(ccKey(channel, cc));
  return v === undefined ? null : v;
}

/** Latest seen value for `cc` on *any* channel — useful when the user
 *  doesn't care which channel emitted it. Returns the most-recent
 *  match (insertion order, so equivalent to "last write wins"). Null
 *  if no message has arrived for this cc on any channel. */
export function getAnyCcValue(cc: number): number | null {
  let last: number | null = null;
  for (const [key, v] of values) {
    const sep = key.indexOf(':');
    if (Number(key.slice(sep + 1)) === cc) last = v;
  }
  return last;
}

/** Record a CC value. Channels are 0-indexed (matching the MIDI byte
 *  layout). `rawValue` is 0..127. Internally normalises to 0..1 and
 *  fans out to subscribers. */
export function setCcValue(
  channel: number,
  cc: number,
  rawValue: number,
  timestamp: number = performance.now(),
): void {
  if (!Number.isFinite(channel) || channel < 0 || channel > 15) return;
  if (!Number.isFinite(cc) || cc < 0 || cc > 127) return;
  if (!Number.isFinite(rawValue) || rawValue < 0 || rawValue > 127) return;
  const value = rawValue / 127;
  values.set(ccKey(channel, cc), value);
  const event: CcEvent = { channel, cc, value, rawValue, timestamp };
  // Snapshot before iterating: the MAP-gesture pattern (subscribe,
  // capture next CC, unsubscribe inside the callback) mutates the set
  // mid-dispatch. Set.delete() during iteration is safe but a defensive
  // copy matches sharedTicker.ts and keeps semantics obvious.
  const frozen = Array.from(listeners);
  for (const listener of frozen) {
    try {
      listener(event);
    } catch {
      // A broken subscriber shouldn't take down the rest. Silent —
      // logging on every dropped message would be noisy at high CC
      // rates from a knob sweep.
    }
  }
}

/** Subscribe to every incoming CC event. Returns an unsubscribe fn. */
export function subscribeCc(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** For tests — wipe state between cases. Do not call from app code. */
export function _resetCcState(): void {
  values.clear();
  listeners.clear();
}
